/**
 * Elite Batch 22 Part 4 — 12 Klaviyo Marketing Workflow Templates (workflows 39-50)
 * Pattern C: Browse Abandonment (39-40)
 * Pattern D: Catalog Alert (41-43)
 * Pattern E: Post-Purchase (44-47)
 * Pattern F: Renewal/Subscription/Replenishment (48-50)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 39. Browse Abandonment (Pattern C) ───
  "Browse Abandonment": {
    summary:
      "When a Klaviyo browse-abandonment event fires (product viewed, no add-to-cart), AI extracts the viewed product details from the webhook payload. The workflow looks up the customer's browsing affinity and purchase history via the Klaviyo Profiles API. Returning customers receive a personalized browse reminder email featuring the exact products they viewed. After a 24-hour delay, if the shopper still has not added anything to cart, a second email is sent featuring AI-generated product recommendations based on their browsing patterns and category preferences.",
    nodes: [
      trigger("Klaviyo: Product Viewed No Cart Event", "webhook_app", {
        source: "klaviyo",
        event: "viewed_product",
        howItStarts:
          "Fires when Klaviyo tracks a product viewed event and the customer does not add to cart within the session",
        setup:
          "Connect your Klaviyo account and enable the Viewed Product metric webhook. Filter to only fire when no Add to Cart event follows within the same session.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Browsed Product Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer first name, product name, product URL, product image URL, product price, and product category. Return JSON regardless of payload format.",
        "Extract browsed product details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          {
            field: "product_image_url",
            as: "product_image_url",
            type: "string",
          },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Fetch Customer Browse History and Profile",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.]klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.last_browse_category",
                customName: "last_browse_category",
                type: "string",
              },
              {
                name: "data[0].id",
                customName: "klaviyo_profile_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Returning Customer (Orders > 0)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greaterThan", value: "0" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Browse Reminder with Viewed Products",
        1000,
        "{customer_email}",
        "Still thinking about {product_name}?",
        "Hi {first_name},\\n\\nWe noticed you were checking out {product_name} (${product_price}).\\n\\nGreat choice! This item is one of our favorites in {product_category}.\\n\\nTake another look: {product_url}\\n\\nNeed help deciding? Reply to this email and our team will be happy to help.\\n\\nHappy shopping!",
      ),
      n("6", "delayAction", "Wait 24 Hours for Cart Activity", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Still No Cart Add?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "equals", value: "0" },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Category Recommendations Email",
        1720,
        "You are an ecommerce email copywriter. Based on the customer's browsed product and category, write a personalized recommendations email featuring 3 similar products in the same category. Include compelling product descriptions and a clear call to action. Return JSON with subject and body fields.",
        "Customer: {first_name}, Browsed: {product_name} at ${product_price} in {product_category}, Browse category preference: {last_browse_category}. Write a product recommendations email.",
        [
          { field: "subject", as: "reco_subject", type: "string" },
          { field: "body", as: "reco_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 40. Category Browse Nurture (Pattern C) ───
  "Category Browse Nurture": {
    summary:
      "When a shopper browses a product category multiple times without adding to cart, Klaviyo fires a category-viewed webhook. AI extracts the category and browsing details, then the workflow fetches the customer's profile and purchase history from Klaviyo. If the customer has previously purchased from this category, they receive a curated collection email highlighting new arrivals. After 24 hours, if they still haven't engaged, AI generates a personalized email featuring top-rated products from that category with social proof and trending badges.",
    nodes: [
      trigger("Klaviyo: Category Browsed Multiple Times", "webhook_app", {
        source: "klaviyo",
        event: "viewed_category",
        howItStarts:
          "Fires when a customer views a product category page multiple times without adding any item to cart",
        setup:
          "Connect Klaviyo and configure the Viewed Category metric to fire a webhook after 2+ category page views with no cart activity in the session.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Category Browse Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer name, category name, category URL, number of category views, and any products viewed within the category. Return JSON regardless of payload format.",
        "Extract category browse details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "category_name", as: "category_name", type: "string" },
          { field: "category_url", as: "category_url", type: "string" },
          { field: "view_count", as: "view_count", type: "number" },
          { field: "products_viewed", as: "products_viewed", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Lookup Customer Purchase History by Category",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.categories_purchased",
                customName: "categories_purchased",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.average_order_value",
                customName: "avg_order_value",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Previously Purchased from This Category?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "total_orders", operator: "greaterThan", value: "0" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Curated Collection Highlights Email",
        1000,
        "{customer_email}",
        "New in {category_name} — picked for you, {first_name}",
        "Hi {first_name},\\n\\nWe noticed you've been exploring our {category_name} collection ({view_count} visits!). Since you're clearly interested, here are some fresh arrivals and bestsellers:\\n\\n{products_viewed}\\n\\nBrowse the full collection: {category_url}\\n\\nAs a returning customer, you get first pick on our newest additions.\\n\\nHappy browsing!",
      ),
      n("6", "delayAction", "Wait 24 Hours for Engagement", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Still No Cart Activity?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "greaterThanOrEqual",
              value: "0",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Top-Rated Product Picks Email",
        1720,
        "You are an ecommerce email strategist. Based on the customer's category interest and browsing behavior, write a compelling email featuring the top 3 products from that category with social proof (star ratings, number of reviews, trending badges). Include urgency elements like limited stock or trending status. Return JSON with subject and body fields.",
        "Customer: {first_name}, Category: {category_name}, Views: {view_count}, Products browsed: {products_viewed}, Avg order value: ${avg_order_value}. Write a top-rated product picks email for this category.",
        [
          { field: "subject", as: "picks_subject", type: "string" },
          { field: "body", as: "picks_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 41. Price Drop Reminder (Pattern D) ───
  "Price Drop Reminder": {
    summary:
      "When a product's price drops in Shopify, a Klaviyo webhook fires with the catalog event data. AI extracts the product details and price change information. The workflow then verifies which customers previously viewed or wishlisted this product via the Klaviyo Profiles API. If the price drop exceeds 15%, a high-urgency email is sent immediately featuring the exact savings amount. After 24 hours, customers who haven't purchased receive an AI-generated follow-up emphasizing scarcity and limited-time pricing with a direct purchase link.",
    nodes: [
      trigger("Klaviyo: Price Drop Detected on Product", "webhook_app", {
        source: "klaviyo",
        event: "price_drop",
        howItStarts:
          "Fires when Klaviyo detects a price reduction on a product that customers have previously viewed or wishlisted",
        setup:
          "Connect Klaviyo with your Shopify catalog feed. Enable price drop triggers in Klaviyo Flows for products with interested subscribers.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Price Drop and Product Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer name, product name, product URL, product image URL, original price, new price, price drop percentage, and product category. Return JSON regardless of payload format.",
        "Extract price drop details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "new_price", as: "new_price", type: "number" },
          { field: "drop_percentage", as: "drop_percentage", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
        ],
      ),
      n("3", "hitApi", "Klaviyo: Verify Customer Interest and Consent", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data[0].attributes.subscriptions.email.marketing.consent",
              customName: "email_consent",
              type: "string",
            },
            {
              name: "data[0].attributes.properties.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data[0].id",
              customName: "klaviyo_profile_id",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Significant Price Drop (> 15%)?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "drop_percentage",
              operator: "greaterThan",
              value: "15",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Immediate Price Drop Alert",
        1000,
        "{customer_email}",
        "Price dropped {drop_percentage}% on {product_name}!",
        "Hi {first_name},\\n\\nGreat news! A product you've been eyeing just dropped in price:\\n\\n{product_name}\\nWas: ${original_price} → Now: ${new_price} (Save {drop_percentage}%!)\\n\\nThis is the lowest price we've offered on this item.\\n\\nGrab it before the price goes back up: {product_url}\\n\\nHappy shopping!",
      ),
      n("6", "delayAction", "Wait 24 Hours for Purchase", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Still Not Purchased?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "greaterThanOrEqual",
              value: "0",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Urgency Follow-Up with Scarcity",
        1720,
        "You are an ecommerce urgency copywriter. Write a follow-up email for a customer who was alerted about a price drop but hasn't purchased yet. Emphasize that the reduced price is limited-time, mention scarcity (other shoppers are buying), and include a direct CTA. Return JSON with subject and body fields.",
        "Customer: {first_name}, Product: {product_name}, Was: ${original_price}, Now: ${new_price}, Drop: {drop_percentage}%, Category: {product_category}, Link: {product_url}. Write an urgency follow-up email.",
        [
          { field: "subject", as: "urgency_subject", type: "string" },
          { field: "body", as: "urgency_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 42. Back-in-Stock Reminder (Pattern D) ───
  "Back-in-Stock Reminder": {
    summary:
      "When an out-of-stock item is restocked in Shopify and Klaviyo fires a back-in-stock webhook, AI extracts the product and inventory details. The workflow looks up all interested customers who signed up for back-in-stock notifications via the Klaviyo API. If the restock quantity is limited (fewer than 50 units), the email includes high-urgency scarcity messaging. After 48 hours, customers who still haven't purchased receive an AI-generated follow-up featuring the restocked item alongside similar products as alternatives in case it sells out again.",
    nodes: [
      trigger("Klaviyo: Back-in-Stock Item Restocked", "webhook_app", {
        source: "klaviyo",
        event: "back_in_stock",
        howItStarts:
          "Fires when a previously out-of-stock product is restocked and Klaviyo has subscribers waiting for this notification",
        setup:
          "Connect Klaviyo with your Shopify store. Enable back-in-stock tracking and configure webhooks for restock events on items with subscriber interest.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Restock and Product Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer name, product name, product URL, product image URL, product price, restock quantity, and product category. Return JSON regardless of payload format.",
        "Extract back-in-stock details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "restock_quantity", as: "restock_quantity", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
        ],
      ),
      n("3", "hitApi", "Klaviyo: Verify Subscriber Interest and Profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data[0].attributes.subscriptions.email.marketing.consent",
              customName: "email_consent",
              type: "string",
            },
            {
              name: "data[0].attributes.properties.waitlist_signup_date",
              customName: "waitlist_date",
              type: "string",
            },
            {
              name: "data[0].id",
              customName: "klaviyo_profile_id",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Limited Restock (< 50 Units)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "restock_quantity", operator: "lessThan", value: "50" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Back-in-Stock Alert to Waitlist",
        1000,
        "{customer_email}",
        "It's back! {product_name} is in stock again",
        "Hi {first_name},\\n\\nThe wait is over! {product_name} is back in stock.\\n\\nPrice: ${product_price}\\n\\nYou signed up to be notified about this item, and we wanted to make sure you're first to know. Only {restock_quantity} units are available.\\n\\nShop now before it sells out again: {product_url}\\n\\nDon't miss out this time!",
      ),
      n("6", "delayAction", "Wait 48 Hours for Purchase", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "Customer Still Hasn't Purchased?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "restock_quantity",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Follow-Up with Similar Alternatives",
        1720,
        "You are an ecommerce email copywriter. Write a follow-up email for a customer who was notified about a back-in-stock item but hasn't purchased. Remind them the item is available, mention remaining stock is limited, and suggest 2-3 similar products as alternatives in case it sells out. Return JSON with subject and body fields.",
        "Customer: {first_name}, Product: {product_name} at ${product_price}, Category: {product_category}, Remaining stock: {restock_quantity} units, Link: {product_url}. Write a back-in-stock follow-up with alternatives.",
        [
          { field: "subject", as: "followup_subject", type: "string" },
          { field: "body", as: "followup_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 43. Low Inventory Alert (Pattern D) ───
  "Low Inventory Alert": {
    summary:
      "When inventory drops below a threshold on a popular product, Klaviyo fires a low-stock webhook. AI extracts the product details and current stock level. The workflow fetches all customers who have recently browsed or wishlisted this product from the Klaviyo Profiles API. If stock is critically low (under 10 units), a high-urgency email with countdown-style language is sent immediately. After 24 hours, if the item is still available, customers who haven't purchased receive an AI-crafted last-chance email emphasizing that only a handful remain.",
    nodes: [
      trigger(
        "Klaviyo: Low Inventory Detected on Browsed Item",
        "webhook_app",
        {
          source: "klaviyo",
          event: "low_inventory",
          howItStarts:
            "Fires when Shopify inventory for a product drops below the configured threshold and Klaviyo has interested subscribers for that item",
          setup:
            "Connect Klaviyo with Shopify. Configure low-inventory alerts to trigger when stock falls below your defined threshold for products with browse or wishlist activity.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract Inventory and Product Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer name, product name, product URL, product price, current stock quantity, original stock level, and product category. Return JSON regardless of payload format.",
        "Extract low inventory alert details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "current_stock", as: "current_stock", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
        ],
      ),
      n("3", "hitApi", "Klaviyo: Fetch Interested Customer Profile", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data[0].attributes.subscriptions.email.marketing.consent",
              customName: "email_consent",
              type: "string",
            },
            {
              name: "data[0].attributes.properties.last_viewed_date",
              customName: "last_viewed_date",
              type: "string",
            },
            {
              name: "data[0].id",
              customName: "klaviyo_profile_id",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "Critically Low Stock (< 10 Units)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "current_stock", operator: "lessThan", value: "10" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Low Inventory Urgency Alert",
        1000,
        "{customer_email}",
        "Only {current_stock} left — {product_name} is almost gone!",
        "Hi {first_name},\\n\\nA product you've been eyeing is running low:\\n\\n{product_name} — ${product_price}\\nOnly {current_stock} units remaining!\\n\\nAt this rate, it could sell out within hours. We wanted to give you a heads-up before it's too late.\\n\\nGrab yours now: {product_url}\\n\\nOnce it's gone, we can't guarantee when it'll be back.",
      ),
      n("6", "delayAction", "Wait 24 Hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Item Still Available?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "current_stock", operator: "greaterThan", value: "0" },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Last-Chance Scarcity Email",
        1720,
        "You are an ecommerce urgency copywriter. Write a last-chance email for a customer about a product with critically low inventory. Emphasize extreme scarcity, mention the exact remaining count is very low, create urgency without being pushy, and include a direct buy link. Return JSON with subject and body fields.",
        "Customer: {first_name}, Product: {product_name} at ${product_price}, Category: {product_category}, Remaining: {current_stock} units, Link: {product_url}. Write a last-chance scarcity email.",
        [
          { field: "subject", as: "lastchance_subject", type: "string" },
          { field: "body", as: "lastchance_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 44. Post-Purchase Thank You (Pattern E) ───
  "Post-Purchase Thank You": {
    summary:
      "When an order is placed in Shopify and Klaviyo fires the order-placed webhook, AI extracts the order details including products, total, and shipping info. The workflow looks up the customer's purchase history and VIP status via Klaviyo. First-time buyers receive a special thank-you email with getting-started tips and a community welcome. After a 5-day delay to allow delivery, AI generates a personalized follow-up email with product care tips and a cross-sell recommendation based on what they purchased, logged to Google Sheets for tracking.",
    nodes: [
      trigger("Klaviyo: Order Placed in Shopify", "webhook_app", {
        source: "klaviyo",
        event: "placed_order",
        howItStarts:
          "Fires when a new order is placed in Shopify and synced to Klaviyo as a Placed Order event",
        setup:
          "Connect Klaviyo with your Shopify store. Ensure the Placed Order metric is active and webhook delivery is enabled.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Order and Customer Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo order webhook payload to find the customer email, customer name, order ID, order total, product names, product categories, shipping address city, and whether this is their first order. Return JSON regardless of payload format.",
        "Extract order details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
          { field: "is_first_order", as: "is_first_order", type: "boolean" },
        ],
      ),
      n("3", "hitApi", "Klaviyo: Lookup Customer Tenure and VIP Status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
          headers: [
            {
              key: "Authorization",
              value: "Klaviyo-API-Key {klaviyo_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
            { key: "revision", value: "2024-02-15" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "data[0].attributes.properties.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.total_revenue",
              customName: "customer_ltv",
              type: "number",
            },
            {
              name: "data[0].attributes.properties.vip_tier",
              customName: "vip_tier",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "First-Time Buyer?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Thank You Email with Getting Started Tips",
        1000,
        "{customer_email}",
        "Thank you for your order, {first_name}! Here's what's next",
        "Hi {first_name},\\n\\nThank you so much for your order #{order_id}!\\n\\nYou purchased: {product_names}\\nOrder total: ${order_total}\\n\\nWe're thrilled to have you as a customer. Here are a few things to know:\\n\\n1. Your order is being prepared and you'll receive tracking info soon\\n2. Join our community for exclusive tips and early access to new products\\n3. Questions? Reply to this email — our team reads every message\\n\\nWelcome to the family!",
      ),
      n("6", "delayAction", "Wait 5 Days for Delivery", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Product Delivered (Post-Delay)?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "order_total", operator: "greaterThan", value: "0" },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Care Tips and Cross-Sell Email",
        1720,
        "You are a post-purchase email specialist. Based on the products purchased and their categories, write an email with product care tips and 2 complementary product recommendations. Make the care tips genuinely helpful (not generic). Return JSON with subject, body, and cross_sell_category fields.",
        "Customer: {first_name}, Purchased: {product_names}, Categories: {product_categories}, Order total: ${order_total}, VIP tier: {vip_tier}, Total lifetime orders: {total_orders}. Write a care tips + cross-sell email.",
        [
          { field: "subject", as: "care_subject", type: "string" },
          { field: "body", as: "care_body", type: "string" },
          {
            field: "cross_sell_category",
            as: "cross_sell_category",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 45. Product Review Request (Pattern E) ───
  "Product Review Request": {
    summary:
      "When Klaviyo detects a fulfilled (delivered) order, a webhook fires and AI extracts the delivery and product details. The workflow fetches the customer's profile from Klaviyo to determine their VIP tier and number of prior reviews. First-time buyers receive a warm review request with a guided prompt. After 7 days, if no review has been submitted, AI generates a follow-up email with a small incentive (discount code for next purchase) and a simplified one-click review link to maximize completion rates.",
    nodes: [
      trigger("Klaviyo: Order Delivered / Fulfilled", "webhook_app", {
        source: "klaviyo",
        event: "fulfilled_order",
        howItStarts:
          "Fires when a Shopify order is marked as fulfilled/delivered and synced to Klaviyo as a Fulfilled Order event",
        setup:
          "Connect Klaviyo with Shopify. Ensure the Fulfilled Order metric is active. Set a flow filter to trigger only after the first fulfillment per order.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Delivery and Product Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo fulfilled order webhook payload to find the customer email, customer name, order ID, product names, product categories, delivery date, and order total. Return JSON regardless of payload format.",
        "Extract fulfilled order details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
          { field: "delivery_date", as: "delivery_date", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Fetch Customer Profile and Review History",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.reviews_submitted",
                customName: "reviews_submitted",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.vip_tier",
                customName: "vip_tier",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "First-Time Buyer (Orders = 1)?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Review Request with Guided Prompt",
        1000,
        "{customer_email}",
        "How are you enjoying {product_names}?",
        "Hi {first_name},\\n\\nYour order #{order_id} was delivered on {delivery_date} — we hope you love it!\\n\\nWe'd love to hear what you think about {product_names}. Your review helps other shoppers make confident decisions.\\n\\nHere's a quick guide to writing a helpful review:\\n- What do you like most about the product?\\n- How does it compare to your expectations?\\n- Would you recommend it to a friend?\\n\\nLeave your review here: https://yourstore.com/review/{order_id}\\n\\nThank you for being part of our community!",
      ),
      n("6", "delayAction", "Wait 7 Days for Review Submission", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "No Review Submitted Yet?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "reviews_submitted",
              operator: "lessThanOrEqual",
              value: "0",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Review Reminder with Incentive",
        1720,
        "You are an ecommerce email copywriter specializing in review requests. Write a friendly follow-up email reminding the customer to leave a review. Include a 10% discount incentive for their next order as a thank-you for reviewing. Make the review process sound quick and easy (under 2 minutes). Return JSON with subject and body fields.",
        "Customer: {first_name}, Products: {product_names}, Categories: {product_categories}, Order: #{order_id}, VIP tier: {vip_tier}, Prior reviews: {reviews_submitted}. Write a review reminder with incentive.",
        [
          { field: "subject", as: "reminder_subject", type: "string" },
          { field: "body", as: "reminder_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 46. Cross-Sell After First Order (Pattern E) ───
  "Cross-Sell After First Order": {
    summary:
      "When a first-time customer places an order via Shopify/Klaviyo, AI extracts the order details and product information. The workflow fetches the customer's profile to confirm it's genuinely their first purchase and checks their browsing preferences. A welcome cross-sell email is sent highlighting complementary products based on what they bought. After 10 days (allowing delivery and product experience), if the customer hasn't made a second purchase, AI generates an educational email about product pairing and care, with embedded cross-sell suggestions tailored to their purchase category.",
    nodes: [
      trigger("Klaviyo: First Order Placed", "webhook_app", {
        source: "klaviyo",
        event: "placed_order",
        howItStarts:
          "Fires when a customer places their first-ever order in Shopify, synced to Klaviyo as a Placed Order event with order count = 1",
        setup:
          "Connect Klaviyo with Shopify. Create a flow with the Placed Order trigger and add a flow filter for Historic Order Count equals 1.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract First Order Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo first-order webhook payload to find the customer email, customer name, order ID, order total, product names, product categories, product prices, and number of items. Return JSON regardless of payload format.",
        "Extract first order details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Verify First Purchase and Browse Preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.last_browse_category",
                customName: "browse_category",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.preferred_categories",
                customName: "preferred_categories",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Confirmed First Purchase (Orders = 1)?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Complementary Products Email",
        1000,
        "{customer_email}",
        "Great picks, {first_name}! Here's what goes perfectly with your order",
        "Hi {first_name},\\n\\nThank you for your first order with us (#{order_id})!\\n\\nYou chose: {product_names}\\n\\nCustomers who purchased from {product_categories} also love these complementary products — they pair perfectly together and enhance the overall experience.\\n\\nCheck out our curated picks: https://yourstore.com/collections/pairs-with/{order_id}\\n\\nAs a new customer, enjoy free shipping on your next order. No code needed — it's automatic!\\n\\nWelcome aboard!",
      ),
      n("6", "delayAction", "Wait 10 Days for Delivery and Experience", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "No Second Purchase Yet?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Educational Cross-Sell Email",
        1720,
        "You are a product education and cross-sell specialist. Write an educational email about getting the most from the products the customer bought, with natural product pairing suggestions embedded in the tips. Include 2-3 complementary products that enhance the purchased items. Make it feel helpful, not salesy. Return JSON with subject and body fields.",
        "Customer: {first_name}, Purchased: {product_names}, Categories: {product_categories}, Order value: ${order_total}, Browse preferences: {preferred_categories}. Write an educational cross-sell email.",
        [
          { field: "subject", as: "education_subject", type: "string" },
          { field: "body", as: "education_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 47. Second Purchase Converter (Pattern E) ───
  "Second Purchase Converter": {
    summary:
      "When a one-time buyer has not made a second purchase within 30 days, Klaviyo triggers a flow for repeat conversion. AI extracts the original purchase details and browsing behavior. The workflow fetches the customer's full profile from Klaviyo to assess their engagement level and product affinity. Engaged customers (opened emails recently) receive a personalized second-purchase nudge with tailored product suggestions. After 14 days, if still no second order, AI generates a stronger offer email with a time-limited discount and curated picks based on their purchase and browse history.",
    nodes: [
      trigger(
        "Klaviyo: First-Purchase Customer 30 Days Post-Order",
        "webhook_app",
        {
          source: "klaviyo",
          event: "placed_order",
          howItStarts:
            "Fires for first-time buyers 30 days after their initial order when no second purchase has been detected in Klaviyo",
          setup:
            "Create a Klaviyo flow triggered by Placed Order with flow filter: Historic Order Count = 1. Add a 30-day time delay as the first flow action, then a conditional split checking if order count is still 1.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract Purchase and Engagement Data",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo webhook payload to find the customer email, customer name, first order ID, first order total, products purchased, product categories, and days since purchase. Return JSON regardless of payload format.",
        "Extract first purchase details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "first_order_id", as: "first_order_id", type: "string" },
          {
            field: "first_order_total",
            as: "first_order_total",
            type: "number",
          },
          { field: "product_names", as: "product_names", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Klaviyo: Fetch Customer Engagement and Product Affinity",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://a.klaviyo.com/api/profiles/?filter=equals(email,'{customer_email}')",
            headers: [
              {
                key: "Authorization",
                value: "Klaviyo-API-Key {klaviyo_api_key}",
              },
              { key: "Content-Type", value: "application/json" },
              { key: "revision", value: "2024-02-15" },
            ],
            body: [],
            variables: [
              {
                name: "data[0].attributes.first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.total_orders",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.email_opens_last_30d",
                customName: "recent_opens",
                type: "number",
              },
              {
                name: "data[0].attributes.properties.preferred_categories",
                customName: "preferred_categories",
                type: "string",
              },
              {
                name: "data[0].attributes.properties.last_browse_category",
                customName: "last_browse_category",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Engaged Customer (Opened Emails Recently)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "recent_opens", operator: "greaterThan", value: "2" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Personalized Second-Purchase Nudge",
        1000,
        "{customer_email}",
        "{first_name}, your next favorite is waiting",
        "Hi {first_name},\\n\\nIt's been a month since you ordered {product_names} — we hope you're loving it!\\n\\nBased on your taste, we think you'll also love these picks from {product_categories} and {last_browse_category}:\\n\\nBrowse your personalized recommendations: https://yourstore.com/for-you/{first_order_id}\\n\\nAs a valued customer, your next order ships free. No code needed.\\n\\nSee something you like?",
      ),
      n("6", "delayAction", "Wait 14 Days for Second Purchase", 1240, {
        delayDetails: { duration: 14, unit: "days" },
      }),
      n("7", "condition", "Still Only One Order?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "total_orders",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Time-Limited Offer Email",
        1720,
        "You are a retention email copywriter. Write a compelling email to convert a one-time buyer into a repeat customer. Include a 15% time-limited discount (expires in 7 days), curated product picks based on their first purchase and browsing patterns, and social proof. Make it feel exclusive, not desperate. Return JSON with subject, body, and discount_code fields.",
        "Customer: {first_name}, First order: {product_names} at ${first_order_total}, Categories: {product_categories}, Browse interest: {last_browse_category}, Preferred categories: {preferred_categories}, Recent email opens: {recent_opens}. Write a second-purchase offer email.",
        [
          { field: "subject", as: "offer_subject", type: "string" },
          { field: "body", as: "offer_body", type: "string" },
          { field: "discount_code", as: "discount_code", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 48. Replenishment Reminder (Pattern F) ───
  "Replenishment Reminder": {
    summary:
      "When the predicted reorder date approaches for a consumable product, Klaviyo fires a webhook based on the order's expected consumption timeline. AI extracts the original order details and calculates the reorder window. The workflow checks the customer's subscription status and payment method via the Stripe Customers API. If the customer has no active subscription (one-time buyer), a reorder reminder email is sent with a one-click reorder link. After 5 days, if no reorder has been placed, AI generates a follow-up email with a small discount and a subscribe-and-save offer to encourage repeat behavior.",
    nodes: [
      trigger("Klaviyo: Predicted Reorder Date Approaching", "webhook_app", {
        source: "klaviyo",
        event: "predicted_reorder",
        howItStarts:
          "Fires when Klaviyo's predictive analytics determines a customer is approaching their expected reorder date for a consumable product",
        setup:
          "Connect Klaviyo with Shopify. Enable Predictive Analytics in Klaviyo and configure a flow triggered by the expected date of next order metric approaching within 7 days.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Reorder and Product Consumption Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo reorder prediction webhook payload to find the customer email, customer name, product name, product URL, last order date, predicted reorder date, product price, and average order frequency in days. Return JSON regardless of payload format.",
        "Extract reorder prediction details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "last_order_date", as: "last_order_date", type: "string" },
          {
            field: "predicted_reorder_date",
            as: "predicted_reorder_date",
            type: "string",
          },
          { field: "product_price", as: "product_price", type: "number" },
          {
            field: "order_frequency_days",
            as: "order_frequency_days",
            type: "number",
          },
        ],
      ),
      n("3", "hitApi", "Stripe: Check Customer Subscription Status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers/search?query=email:'{customer_email}'",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].id",
              customName: "stripe_customer_id",
              type: "string",
            },
            {
              name: "data[0].subscriptions.data[0].status",
              customName: "subscription_status",
              type: "string",
            },
            {
              name: "data[0].subscriptions.data[0].id",
              customName: "subscription_id",
              type: "string",
            },
            {
              name: "data[0].default_source",
              customName: "has_payment_method",
              type: "string",
            },
          ],
        },
      }),
      n("4", "condition", "No Active Subscription (One-Time Buyer)?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "subscription_status",
              operator: "notEquals",
              value: "active",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Replenishment Reminder Email",
        1000,
        "{customer_email}",
        "Time to restock, {first_name}? Your {product_name} is running low",
        "Hi {customer_name},\\n\\nBased on your purchase history, it's about time to reorder {product_name}.\\n\\nYou last ordered on {last_order_date}, and we estimate you'll need a refill around {predicted_reorder_date}.\\n\\nReorder now at ${product_price}: {product_url}\\n\\nPro tip: Set up Subscribe & Save to get 10% off every order and never run out again.\\n\\nNeed to adjust your timing? Just reply to this email and we'll update your preferences.",
      ),
      n("6", "delayAction", "Wait 5 Days for Reorder", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Still No Reorder Placed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "subscription_status",
              operator: "notEquals",
              value: "active",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Discount and Subscribe-Save Offer",
        1720,
        "You are a retention email specialist focused on replenishment products. Write a follow-up email offering a 10% discount on a one-time reorder plus a subscribe-and-save option at 15% off ongoing. Emphasize convenience, never running out, and the savings over time. Return JSON with subject and body fields.",
        "Customer: {customer_name}, Product: {product_name} at ${product_price}, Last ordered: {last_order_date}, Average reorder: every {order_frequency_days} days, Link: {product_url}. Write a replenishment follow-up with discount and subscription offer.",
        [
          { field: "subject", as: "followup_subject", type: "string" },
          { field: "body", as: "followup_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 49. Subscription Renewal Reminder (Pattern F) ───
  "Subscription Renewal Reminder": {
    summary:
      "When a subscription renewal is approaching in Stripe, Klaviyo fires a renewal-due webhook. AI extracts the subscription details including plan, price, and renewal date. The workflow checks the payment method status via the Stripe API to determine if the card on file is still valid. If the payment method is expiring or missing, a payment-update-required email is sent with a direct link to update billing info. After 3 days, if the payment method is still not updated, AI generates a value-recap email reminding the customer why their subscription matters and urging them to update payment to avoid interruption.",
    nodes: [
      trigger("Klaviyo: Subscription Renewal Approaching", "webhook_app", {
        source: "klaviyo",
        event: "subscription_renewal_due",
        howItStarts:
          "Fires when a customer's subscription renewal date is 14 days away, tracked by Klaviyo via Stripe integration",
        setup:
          "Connect Klaviyo with Stripe. Configure a flow triggered by the upcoming subscription renewal event with a 14-day advance window.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Subscription Renewal Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo subscription webhook payload to find the customer email, customer name, subscription plan name, subscription price, renewal date, billing cycle, and payment method last four digits. Return JSON regardless of payload format.",
        "Extract subscription renewal details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "plan_price", as: "plan_price", type: "number" },
          { field: "renewal_date", as: "renewal_date", type: "string" },
          { field: "billing_cycle", as: "billing_cycle", type: "string" },
          { field: "card_last_four", as: "card_last_four", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Stripe: Verify Payment Method and Subscription Health",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/customers/search?query=email:'{customer_email}'",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              {
                name: "data[0].id",
                customName: "stripe_customer_id",
                type: "string",
              },
              {
                name: "data[0].invoice_settings.default_payment_method",
                customName: "default_payment_method",
                type: "string",
              },
              {
                name: "data[0].subscriptions.data[0].status",
                customName: "subscription_status",
                type: "string",
              },
              {
                name: "data[0].subscriptions.data[0].current_period_end",
                customName: "period_end",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Payment Method Missing or Expiring?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "default_payment_method",
              operator: "equals",
              value: "",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Renewal Reminder with Payment Update CTA",
        1000,
        "{customer_email}",
        "Your {plan_name} subscription renews on {renewal_date}",
        "Hi {customer_name},\\n\\nJust a heads up — your {plan_name} subscription (${plan_price}/{billing_cycle}) renews on {renewal_date}.\\n\\nWe noticed your payment method may need updating to ensure uninterrupted service. Please verify your billing details:\\n\\nUpdate payment method: https://yourstore.com/account/billing\\n\\nCurrent card on file: ending in {card_last_four}\\n\\nIf everything looks good, no action needed — your subscription will renew automatically.\\n\\nQuestions about your plan? Reply to this email and we'll help.",
      ),
      n("6", "delayAction", "Wait 3 Days for Payment Update", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "Payment Still Not Updated?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "default_payment_method",
              operator: "equals",
              value: "",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Value Recap and Urgency Email",
        1720,
        "You are a subscription retention specialist. Write a follow-up email that recaps the value the customer gets from their subscription, reminds them their renewal is imminent, and creates urgency to update their payment method. Mention specific benefits they'd lose if the subscription lapses. Return JSON with subject and body fields.",
        "Customer: {customer_name}, Plan: {plan_name} at ${plan_price}/{billing_cycle}, Renewal date: {renewal_date}, Card: ending {card_last_four}, Status: {subscription_status}. Write a value recap and payment-update urgency email.",
        [
          { field: "subject", as: "urgency_subject", type: "string" },
          { field: "body", as: "urgency_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ─── 50. Subscription Cancel Save (Pattern F) ───
  "Subscription Cancel Save": {
    summary:
      "When a customer initiates a subscription cancellation in Stripe, Klaviyo fires a cancel-initiated webhook. AI extracts the cancellation details including the reason and subscription tenure. The workflow checks the customer's full billing history and lifetime value via Stripe. High-value customers (LTV above $200) receive a personalized save offer email with an incentive to stay — such as a discount, pause option, or plan downgrade. After 5 days, if the cancellation is still pending, AI generates an exit survey email that also includes a final compelling offer, aiming to retain the customer or gather actionable feedback for future improvements.",
    nodes: [
      trigger("Klaviyo: Subscription Cancellation Initiated", "webhook_app", {
        source: "klaviyo",
        event: "subscription_cancel_initiated",
        howItStarts:
          "Fires when a customer initiates a subscription cancellation in Stripe, synced to Klaviyo as a cancel event before the subscription actually ends",
        setup:
          "Connect Klaviyo with Stripe. Configure a flow triggered by the subscription cancellation initiated event. Ensure it fires during the cancellation grace period, not after final cancellation.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract Cancellation and Subscription Details",
        280,
        "You are a data extraction assistant. Parse the incoming Klaviyo cancellation webhook payload to find the customer email, customer name, subscription plan name, subscription price, cancellation reason, subscription start date, billing cycle, and cancellation effective date. Return JSON regardless of payload format.",
        "Extract subscription cancellation details from this Klaviyo webhook payload: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "plan_price", as: "plan_price", type: "number" },
          { field: "cancel_reason", as: "cancel_reason", type: "string" },
          {
            field: "subscription_start_date",
            as: "subscription_start_date",
            type: "string",
          },
          { field: "billing_cycle", as: "billing_cycle", type: "string" },
          {
            field: "cancel_effective_date",
            as: "cancel_effective_date",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "Stripe: Fetch Customer LTV and Billing History", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers/search?query=email:'{customer_email}'",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].id",
              customName: "stripe_customer_id",
              type: "string",
            },
            {
              name: "data[0].metadata.lifetime_value",
              customName: "customer_ltv",
              type: "number",
            },
            {
              name: "data[0].subscriptions.data[0].status",
              customName: "subscription_status",
              type: "string",
            },
            {
              name: "data[0].subscriptions.data[0].cancel_at_period_end",
              customName: "cancel_at_period_end",
              type: "boolean",
            },
          ],
        },
      }),
      n("4", "condition", "High-Value Customer (LTV > $200)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "customer_ltv", operator: "greaterThan", value: "200" },
          ],
        },
      }),
      emailNode(
        "5",
        "Klaviyo: Send Personalized Save Offer Email",
        1000,
        "{customer_email}",
        "We'd hate to see you go, {customer_name} — can we make it right?",
        "Hi {customer_name},\\n\\nWe noticed you're thinking about canceling your {plan_name} subscription (${plan_price}/{billing_cycle}).\\n\\nWe understand, and we want to help. Here are some options before you go:\\n\\n1. Pause your subscription — Take a break for up to 3 months, no charge\\n2. Switch to a lighter plan — Same great features at a lower price point\\n3. Get 25% off your next 3 months — Stay and save\\n\\nYou've been a subscriber since {subscription_start_date} and we truly value your loyalty.\\n\\nChoose your option: https://yourstore.com/account/save-offer\\n\\nOr reply to this email and let us know how we can make things better.",
      ),
      n("6", "delayAction", "Wait 5 Days Before Final Outreach", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Cancellation Still Pending?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "cancel_at_period_end",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),
      aiNode(
        "8",
        "OpenAI: Generate Exit Survey with Final Offer",
        1720,
        "You are a subscription retention specialist. Write a final email that includes a short exit survey (3 questions) to understand why the customer is leaving, plus one last compelling save offer. Be empathetic, not pushy. Acknowledge their decision while making a genuine final attempt. Include links to a survey form. Return JSON with subject and body fields.",
        "Customer: {customer_name}, Plan: {plan_name} at ${plan_price}/{billing_cycle}, Cancel reason: {cancel_reason}, Subscriber since: {subscription_start_date}, LTV: ${customer_ltv}, Cancel date: {cancel_effective_date}. Write an exit survey + final save offer email.",
        [
          { field: "subject", as: "exit_subject", type: "string" },
          { field: "body", as: "exit_body", type: "string" },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },
};
